import type { AccountInfo } from "@peerup/auth-kit"

import type { Entrance, VerifyCodeType } from "@/enums/statusEnum"
import type {
  ApplyJoinByTokenRequest,
  ApplyJoinByTokenResponse,
  ApplyJoinOrgRequest,
  BindCodeResponse,
  CodeLoginParams,
  CreateOrgByCodeRequest,
  CreateOrgByTokenParams,
  JoinAccountResponse,
  PwdLoginParams,
  QueryInviteInfoResult,
  RegisterParams,
  RegisterResponse,
} from "@/types/login.types"

import { request } from "@/utils/request"
import { extractAccountIdFromUrl } from "@/utils/url"

// /api/user/account/email/sendCode  发送验证码 post  验证码类型; 1-验证码登录，2-创建组织，3-申请加入组织，4-绑定邮箱，5-解绑邮箱
export function sendCodeApi(data: { email: string; codeType: VerifyCodeType }) {
  return request({
    url: "/api/user/account/sendCode", // 发送邮箱验证码
    method: "post",
    data,
  })
}

// 获取手机验证码 entrance = login(登录）/ bind（绑定）/register(注册）/ notify（修改）
export function getSMSCodeApi(params: { phone: string; entrance: Entrance }) {
  return request({
    url: "api/user/consumer/getSMSCode",
    method: "get",
    params,
  })
}

// 发送短信验证码,未登录组织状态 /api/user/account/sendSmsCode
export function sendSmsCodeApi(data: { phone: string; codeType: number }) {
  return request({
    url: "/api/user/account/sendSmsCode",
    method: "post",
    data,
  })
}

// /api/user/account/email/codeLogin 验证码登录 post
export function codeLoginApi(data: CodeLoginParams, traceId?: string) {
  return request({
    url: "api/user/account/codeLogin",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// /api/user/account/email/pwdLogin 密码登录 post
export function pwdLoginApi(data: PwdLoginParams, traceId?: string) {
  return request({
    url: "api/user/account/pwdLogin",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// 未选中组织状态通过token创建组织
export function createOrgByTokenApi(data: CreateOrgByTokenParams, traceId?: string) {
  return request<AccountInfo>({
    url: "/api/user/account/createOrgByToken",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// 查询组织邀请链接信息
export function queryInviteInfoApi(params: { inviteCode: string; email?: string }) {
  params.inviteCode = extractAccountIdFromUrl(params.inviteCode)
  return request<QueryInviteInfoResult>({
    url: "/api/user/account/queryInviteInfo",
    method: "get",
    params,
  })
}

// 查询指定成员邀请链接信息接口
export function queryBindInfoApi(params: { inviteCode: string }) {
  params.inviteCode = extractAccountIdFromUrl(params.inviteCode)
  return request<BindCodeResponse>({
    url: "/api/user/account/queryBindInfo",
    method: "get",
    params,
  })
}

// 邮箱验证码申请加入组织
export function applyJoinByCodeApi(data: ApplyJoinOrgRequest, traceId?: string) {
  data.inviteCode = extractAccountIdFromUrl(data.inviteCode)
  return request<JoinAccountResponse>({
    url: "/api/user/account/applyJoinByCode",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

/**
 * 选中组织状态通过邮箱验证码创建组织
 * @param data - 创建组织的请求参数
 * @returns Promise<CreateOrgByCodeResponse> - 返回创建的组织信息
 */
export function createOrgByCodeApi(data: CreateOrgByCodeRequest, traceId?: string) {
  return request<AccountInfo>({
    url: "/api/user/account/createOrgByCode",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// 未选中组织临时token申请加入组织
export function applyJoinByTokenApi(data: ApplyJoinByTokenRequest, traceId?: string) {
  return request<ApplyJoinByTokenResponse>({
    url: "/api/user/account/applyJoinByToken",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// 注册
export function register(data: RegisterParams) {
  return request<RegisterResponse>({
    url: "/api/user/consumer/signUp",
    method: "post",
    data,
  })
}

// 初始化登录密码  /api/user/member/initPwd passWord 新密码
export function initPwdApi(data: { passWord: string }, token: string) {
  return request({
    url: "/api/user/member/initPwd",
    method: "post",
    data,
    headers: {
      Authorization: token,
    },
  })
}

// 更新密码 Data里token 验证码得到token   // 第二个token 是账号token
export function updatePwdApi(data: { passWord: string; token: string }, token: string) {
  return request({
    url: "api/user/member/updatePwd",
    method: "post",
    data,
    headers: {
      Authorization: token,
    },
  })
}

// 初始化绑定手机号 /api/user/member/bindPhone
export function bindPhoneApi(data: { phone: string; code: string }, token: string) {
  return request({
    url: "/api/user/member/bindPhone",
    method: "post",
    data,
    headers: {
      Authorization: token,
    },
  })
}

//  /api/user/member/sendCode 发送验证码
export function verifySendCodeApi(data: { identifier: string; verType: string; codeType: VerifyCodeType }, token: string) {
  return request({
    url: "/api/user/member/sendCode",
    method: "post",
    data,
    headers: {
      Authorization: token, // 组织token
    },
  })
}

// 飞书权限校验 api/user/callback/feishu/auth
export function getFeiShuAuthApi(data: { code: string; state: string; redirectUri: string; keepAlive: boolean; browserKey: string; path: string }, traceId?: string) {
  return request({
    url: "api/user/callback/feishu/auth",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

type UumAuthApiResponse = Pick<AccountInfo, "token" | "memberGuid" | "orgGuid" | "orgName" | "userName" | "userGuid" | "orgType" | "roleType" | "avatar" | "orgAvatar" | "color" | "opened" | "loginStatus" | "identifier" | "ipLimit" | "joinTime" | "loginType"> & {
  jumpUrl: string
}

export function getUumAuthApi(data: { code: string; state: string; clientId: string; keepAlive: boolean; browserKey: string; path: string }, traceId?: string) {
  return request<UumAuthApiResponse>({
    url: "/api/user/callback/uum/login",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  }).then(data => {
    return {
      ...data,
      sessionLogin: false,
      userId: data.userGuid,
    } as AccountInfo & { jumpUrl: string }
  })
}

// 邀请指定成员发送邮箱验证码 /api/user/account/sendBindCode post
export function sendBindCodeApi(data: { inviteCode: string }) {
  data.inviteCode = extractAccountIdFromUrl(data.inviteCode)
  return request({
    url: "/api/user/account/sendBindCode",
    method: "post",
    data,
  })
}
type BindCodeLoginParams = { browserKey: string; path: string; loginType: string; keepAlive: boolean; inviteCode: string; code: string }
// 邀请指定成员登录  /api/user/account/bindCodeLogin post
export function bindCodeLoginApi(data: BindCodeLoginParams, traceId?: string) {
  data.inviteCode = extractAccountIdFromUrl(data.inviteCode)
  return request({
    url: "/api/user/account/bindCodeLogin",
    method: "post",
    data,
    headers: {
      "X-Trace-Id": traceId,
    },
  })
}

// 预检测账号是否存在 /api/eru/account/preDetection post
export function preDetectionApi(data: { phone: string }) {
  return request({
    url: "/api/user/account/preDetection",
    method: "post",
    data,
  })
}
/**
 * 预较验邮箱/手机号登录流程，判断邮箱/手机号是否有绑定的账号
 */
export const preCheckEmailApi = (data: { email: string }) => {
  return request<boolean>({
    url: "/api/user/account/loginProcess",
    method: "post",
    data,
  })
}

export const getUumAuthUrlApi = (data: {
  clientId: string
  /**
   * 跳转URL，用于存储此次授权后，回调到指定页面（服务端存储）
   */
  jumpUrl: string
}) => {
  return request<{
    authUrl: string
  }>({
    url: "/api/user/callback/uum/authUrl",
    method: "post",
    data,
  })
}

// 判断当前登录用户是否为官方账号
export function isOfficialAccountApi() {
  return request<boolean>({
    url: "/api/user/member/isOfficialAccount",
    method: "get",
  })
}
